import { useState, useRef } from 'react';
import { fetchQuizBatch } from '../api';
import { QUIZ_TOPICS } from '../data/topics';

const LETTERS = ['A', 'B', 'C', 'D'];
const HINTS = ['Consulting the AIM...','Checking FARs...','Reviewing sectional charts...','Briefing weather...'];

function LoadingCard() {
  const hint = HINTS[Math.floor(Math.random() * HINTS.length)];
  return (
    <div className="loading-card">
      <div className="spinner" />
      <div className="loading-text">GENERATING<span className="blink">_</span></div>
      <div className="loading-sub">{hint}</div>
    </div>
  );
}

function MilestoneCard({ current, score, onContinue, onChangeTopic }) {
  const pct = Math.round((score / current) * 100);
  const color = pct >= 80 ? 'var(--green)' : pct >= 70 ? 'var(--amber)' : 'var(--red)';
  const emoji = pct >= 80 ? '🎯' : pct >= 70 ? '📈' : '📚';
  const msg   = pct >= 80 ? 'Excellent — above FAA passing threshold!' : pct >= 70 ? 'At passing level — keep pushing.' : 'Below 70% FAA minimum. Review weak areas.';
  return (
    <div className="quiz-card" style={{ textAlign: 'center', padding: '44px 28px', animation: 'fadeUp .2s ease' }}>
      <div style={{ fontSize: 44, marginBottom: 14 }}>{emoji}</div>
      <div className="quiz-category">CHECKPOINT — {current} QUESTIONS</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Running Score</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 48, fontWeight: 500, color, lineHeight: 1, margin: '14px 0' }}>{pct}%</div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>{score} correct of {current} questions</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 26 }}>{msg}</div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button className="qa-btn secondary" onClick={onChangeTopic}>Change Topic</button>
        <button className="qa-btn primary" onClick={onContinue}>Keep Going →</button>
      </div>
    </div>
  );
}

export default function PracticeQuiz() {
  const [phase, setPhase]         = useState('select'); // select | loading | quiz | milestone
  const [topic, setTopic]         = useState('');
  const [topicLabel, setTopicLabel] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent]     = useState(0);
  const [score, setScore]         = useState(0);
  const [selected, setSelected]   = useState(null);
  const [answered, setAnswered]   = useState(false);
  const [error, setError]         = useState(null);
  const prefetchedRef = useRef([]);
  const fetchingRef   = useRef(false);

  const startQuiz = async (t, label) => {
    setTopic(t); setTopicLabel(label);
    setQuestions([]); setCurrent(0); setScore(0);
    setSelected(null); setAnswered(false); setError(null);
    prefetchedRef.current = []; fetchingRef.current = false;
    setPhase('loading');
    try {
      const batch = await fetchQuizBatch(t, []);
      setQuestions(batch);
      setPhase('quiz');
      prefetch(t, batch);
    } catch (e) { setError(e.message); setPhase('select'); }
  };

  const prefetch = (t, prev) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    fetchQuizBatch(t, prev)
      .then((b) => { prefetchedRef.current = b; fetchingRef.current = false; })
      .catch(() => { fetchingRef.current = false; });
  };

  const loadNextBatch = async (allQ) => {
    setPhase('loading');
    try {
      let batch;
      if (prefetchedRef.current.length > 0) {
        batch = prefetchedRef.current; prefetchedRef.current = [];
      } else {
        batch = await fetchQuizBatch(topic, allQ);
      }
      const updated = [...allQ, ...batch];
      setQuestions(updated);
      setPhase('quiz');
      prefetch(topic, updated);
    } catch (e) { setError(e.message); }
  };

  const handleCheck = () => {
    if (selected === null) return;
    setAnswered(true);
    if (selected === questions[current].correct) setScore((s) => s + 1);
  };

  const handleNext = async () => {
    const nextIdx = current + 1;

    // Milestone every 10 questions
    if (nextIdx > 0 && nextIdx % 10 === 0) {
      setCurrent(nextIdx);
      setPhase('milestone');
      return;
    }

    if (nextIdx >= questions.length) {
      await loadNextBatch(questions);
      setCurrent(nextIdx);
    } else {
      setCurrent(nextIdx);
      if (nextIdx >= questions.length - 2 && prefetchedRef.current.length === 0) {
        prefetch(topic, questions);
      }
    }
    setSelected(null); setAnswered(false);
    if (phase !== 'milestone') setPhase('quiz');
  };

  const q = questions[current];

  if (phase === 'select') {
    return (
      <div className="quiz-wrap fade-up">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>AI Practice Quiz</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>Powered by Claude — Unlimited Questions</div>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--amber)' }}>🔥 82% avg</div>
        </div>

        {error && <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 7, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--red)', marginBottom: 14 }}>{error}</div>}

        <div className="section-label" style={{ marginBottom: 14 }}>Choose a Topic</div>
        <div className="topic-grid">
          {QUIZ_TOPICS.map(({ topic: t, label, icon, desc }) => (
            <div key={label} className="topic-card" onClick={() => startQuiz(t, label)}>
              <div className="topic-icon">{icon}</div>
              <div className="topic-name">{label}</div>
              <div className="topic-desc">{desc}</div>
            </div>
          ))}
          <div
            className="topic-card topic-card-special"
            onClick={() => startQuiz('Mixed private pilot written exam topics covering all subject areas', 'Full Exam Sim')}
          >
            <div className="topic-icon">🎯</div>
            <div>
              <div className="topic-name">Full Exam Simulator</div>
              <div className="topic-desc">All topics — closest to the real PAR test</div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div className="section-label" style={{ marginBottom: 10 }}>Custom Topic</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              type="text"
              placeholder="e.g. Emergency procedures, weight & balance, night VFR..."
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && customTopic.trim() && startQuiz(customTopic.trim(), customTopic.trim())}
              style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 14px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }}
              onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
              onBlur={(e)  => e.target.style.borderColor = 'var(--border)'}
            />
            <button
              className="cta-btn"
              style={{ padding: '10px 18px', borderRadius: 7 }}
              onClick={() => customTopic.trim() && startQuiz(customTopic.trim(), customTopic.trim())}
            >
              GENERATE →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'loading') return <div className="quiz-wrap fade-up"><LoadingCard /></div>;

  if (phase === 'milestone') {
    return (
      <div className="quiz-wrap fade-up">
        <MilestoneCard
          current={current}
          score={score}
          onContinue={() => { setPhase('quiz'); setSelected(null); setAnswered(false); }}
          onChangeTopic={() => setPhase('select')}
        />
      </div>
    );
  }

  if (!q) return null;

  const progressPct = ((current % 5) + 1) / 5 * 100;

  return (
    <div className="quiz-wrap fade-up">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text2)', fontFamily: 'var(--mono)', fontSize: 11, padding: '6px 12px', borderRadius: 5, cursor: 'pointer' }}
          onClick={() => setPhase('select')}
        >
          ← Dashboard
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>AI Practice Quiz</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{topicLabel}</div>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--amber)' }}>🔥 82% avg</div>
      </div>

      <div className="quiz-header">
        <div className="quiz-counter">Q {current + 1} / ∞</div>
        <div className="quiz-progress-bar"><div className="quiz-progress-fill" style={{ width: `${progressPct}%` }} /></div>
        <button
          style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 10, padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}
          onClick={() => setPhase('select')}
        >
          CHANGE TOPIC
        </button>
      </div>

      <div className="quiz-card" style={{ animation: 'fadeUp .2s ease' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div className="quiz-category">{q.category}</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)' }}>{score}/{current} correct</div>
        </div>
        <div className="quiz-question">{q.question}</div>
        <div className="quiz-options">
          {q.options.map((opt, i) => {
            let cls = '';
            if (answered) {
              if (i === q.correct) cls = 'correct';
              else if (i === selected) cls = 'incorrect';
            } else if (i === selected) cls = 'selected';
            return (
              <button key={i} className={`quiz-option ${cls}`} onClick={() => !answered && setSelected(i)} disabled={answered}>
                <div className="opt-letter">{LETTERS[i]}</div><span>{opt}</span>
              </button>
            );
          })}
        </div>
        {answered && (
          <div className="quiz-explanation">
            <strong>✓ Explanation:</strong>{' '}
            <span dangerouslySetInnerHTML={{ __html: q.explanation }} />
          </div>
        )}
      </div>

      <div className="quiz-actions">
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, background: 'var(--green)', borderRadius: '50%', display: 'inline-block' }} />
          AI-generated · infinite questions
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!answered && <button className="qa-btn primary" onClick={handleCheck} disabled={selected === null}>CHECK ANSWER</button>}
          {answered  && <button className="qa-btn secondary" onClick={handleNext}>NEXT →</button>}
        </div>
      </div>
    </div>
  );
}
